/**
 * LumaSkill Transformers
 *
 * Bidirectional transformations between:
 * - OOTB Luma format (from Python generator)
 * - Canonical LumaSkill format (our internal representation)
 * - React Flow format (for visualization)
 */

import dagre from 'dagre';
import type { Node, Edge } from 'reactflow';
import {
  type LumaSkill,
  type LumaSkillNode,
  type LumaSkillLink,
  type ValidationResult,
  NodeType,
  OOTB_NODE_TYPE_MAP,
  NODE_TYPE_TO_OOTB
} from '@/types/lumaSkill';

/**
 * Parse error thrown when transformation fails
 */
export class TransformError extends Error {
  constructor(message: string, public readonly details?: any) {
    super(message);
    this.name = 'TransformError';
  }
}

/**
 * React Flow node data structure
 */
export interface ReactFlowNodeData {
  label: string;
  nodeType: NodeType;
  properties: Record<string, any>;
  validation?: any[];
}

/**
 * Main transformer class with static methods
 */
export class LumaSkillTransformers {
  /**
   * Parse OOTB Luma format (from Python generator) to canonical LumaSkill
   *
   * @param ootbSkill - Raw skill JSON from Python generator
   * @returns Canonical LumaSkill representation
   */
  static fromOOTBFormat(ootbSkill: any): LumaSkill {
    try {
      // Check if this is the new format (nodes/connections at top level)
      if (ootbSkill.nodes && ootbSkill.connections && !ootbSkill.designerFlowDefinition) {
        return this.fromNewOOTBFormat(ootbSkill);
      }

      // Legacy format with designerFlowDefinition
      // Extract metadata
      const identifier = ootbSkill.identifier || '';
      const name = ootbSkill.name || '';
      const description = ootbSkill.description || '';

      // Extract cells from designerFlowDefinition
      const cells = ootbSkill.designerFlowDefinition?.cells || [];
      if (!Array.isArray(cells)) {
        throw new TransformError('Invalid cells array in designerFlowDefinition');
      }

      // Separate nodes and links
      const nodeCells = cells.filter((cell: any) => cell.type !== 'link');
      const linkCells = cells.filter((cell: any) => cell.type === 'link');

      // Transform nodes
      const nodes: LumaSkillNode[] = nodeCells.map((cell: any) => {
        const nodeType = OOTB_NODE_TYPE_MAP[cell.type];
        if (!nodeType) {
          console.warn(`Unknown node type: ${cell.type}, skipping...`);
          return null;
        }

        return {
          id: cell.id,
          type: nodeType,
          name: cell.definition?.name || cell.type,
          position: {
            x: cell.position?.x || 0,
            y: cell.position?.y || 0
          },
          properties: this.extractNodeProperties(cell)
        };
      }).filter(Boolean) as LumaSkillNode[];

      // Transform links
      const links: LumaSkillLink[] = linkCells.map((cell: any) => ({
        id: cell.id,
        source: cell.source?.id || '',
        target: cell.target?.id || '',
        sourcePort: cell.source?.port,
        targetPort: cell.target?.port,
        label: cell.labels?.[0]?.attrs?.text?.text
      }));

      // Extract validation results if present
      const validation: ValidationResult | undefined = ootbSkill.validation ? {
        confidence: ootbSkill.validation.confidence || 0,
        errors: ootbSkill.validation.errors || [],
        warnings: ootbSkill.validation.warnings || [],
        passed: ootbSkill.validation.passed || []
      } : undefined;

      return {
        identifier,
        name,
        description,
        nodes,
        links,
        validation
      };
    } catch (error: any) {
      throw new TransformError(
        `Failed to parse OOTB format: ${error.message}`,
        error
      );
    }
  }

  /**
   * Parse new OOTB format (nodes/connections at top level)
   *
   * @param ootbSkill - Raw skill JSON from Python generator (new format)
   * @returns Canonical LumaSkill representation
   */
  private static fromNewOOTBFormat(ootbSkill: any): LumaSkill {
    try {
      // Extract metadata
      const metadata = ootbSkill.metadata || {};
      const identifier = metadata.identifier || '';
      const name = metadata.name || '';
      const description = metadata.description || '';

      // Transform nodes from new format
      const nodes: LumaSkillNode[] = (ootbSkill.nodes || []).map((node: any) => {
        const resolveNodeType = (): NodeType | undefined => {
          if (node.cellType && OOTB_NODE_TYPE_MAP[node.cellType]) {
            return OOTB_NODE_TYPE_MAP[node.cellType];
          }
          if (typeof node.type === 'string' && OOTB_NODE_TYPE_MAP[node.type]) {
            return OOTB_NODE_TYPE_MAP[node.type];
          }
          if (typeof node.type === 'string') {
            const lower = node.type.toLowerCase();
            const matchedKey = Object.keys(OOTB_NODE_TYPE_MAP).find((key) => key.toLowerCase() === lower);
            if (matchedKey) {
              return OOTB_NODE_TYPE_MAP[matchedKey];
            }
          }
          if (typeof node.cellType === 'string') {
            const lower = node.cellType.toLowerCase();
            const matchedKey = Object.keys(OOTB_NODE_TYPE_MAP).find((key) => key.toLowerCase() === lower);
            if (matchedKey) {
              return OOTB_NODE_TYPE_MAP[matchedKey];
            }
          }
          return undefined;
        };

        const nodeType = resolveNodeType();
        if (!nodeType) {
          console.warn(`Unknown node type/cellType: ${node.type || node.cellType}, skipping...`);
          return null;
        }

        return {
          id: node.id,
          type: nodeType,
          name: node.name || node.type,
          position: {
            x: node.position?.x || 0,
            y: node.position?.y || 0
          },
          properties: {
            cellType: node.cellType,
            type: node.type,
            size: node.size,
            ...node.properties
          }
        };
      }).filter(Boolean) as LumaSkillNode[];

      // Transform connections to links
      const links: LumaSkillLink[] = (ootbSkill.connections || []).map((conn: any) => ({
        id: conn.id,
        source: conn.source?.cell || '',
        target: conn.target?.cell || '',
        sourcePort: conn.source?.port,
        targetPort: conn.target?.port,
        label: conn.label
      }));

      return {
        identifier,
        name,
        description,
        nodes,
        links
      };
    } catch (error: any) {
      throw new TransformError(
        `Failed to parse new OOTB format: ${error.message}`,
        error
      );
    }
  }

  /**
   * Extract node properties from OOTB cell definition
   */
  private static extractNodeProperties(cell: any): Record<string, any> {
    const def = cell.definition || {};
    const properties: Record<string, any> = {};

    // Common properties
    if (def.text) properties.text = def.text;
    if (def.prompt) properties.prompt = def.prompt;
    if (def.buttons) properties.buttons = def.buttons;
    if (def.rules) properties.rules = def.rules;
    if (def.attributeName) properties.attributeName = def.attributeName;
    if (def.attributeValue) properties.attributeValue = def.attributeValue;
    if (def.url) properties.url = def.url;
    if (def.method) properties.method = def.method;
    if (def.taskDescription) properties.taskDescription = def.taskDescription;
    if (def.skillIdentifier) properties.skillIdentifier = def.skillIdentifier;
    if (def.script) properties.script = def.script;
    if (def.knowledgeBase) properties.knowledgeBase = def.knowledgeBase;
    if (def.knowledgeBaseId) properties.knowledgeBaseId = def.knowledgeBaseId;
    if (def.searchQuery) properties.searchQuery = def.searchQuery;
    if (def.displayMode) properties.displayMode = def.displayMode;
    if (def.responseTemplate) properties.responseTemplate = def.responseTemplate;

    // Store full definition for reference
    properties._fullDefinition = def;

    return properties;
  }

  /**
   * Convert canonical LumaSkill to React Flow format
   *
   * @param skill - Canonical LumaSkill
   * @returns React Flow nodes and edges
   */
  static toReactFlow(skill: LumaSkill): { nodes: Node<ReactFlowNodeData>[]; edges: Edge[] } {
    try {
      // Transform nodes
      const nodes: Node<ReactFlowNodeData>[] = skill.nodes.map((node) => ({
        id: node.id,
        type: 'custom', // We'll use custom node components
        position: node.position,
        data: {
          label: node.name,
          nodeType: node.type,
          properties: node.properties,
          validation: [] // TODO: Add node-specific validation issues
        }
      }));

      // Transform links to edges
      const edges: Edge[] = skill.links
        .filter((link) => link.source && link.target && link.source !== link.target)
        .map((link) => {
          const sanitizeHandle = (value?: string | null) => {
            if (!value) return undefined;
            const trimmed = value.trim();
            if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
              return undefined;
          }
          return trimmed;
        };

        return {
          id: link.id,
          source: link.source,
          target: link.target,
          sourceHandle: sanitizeHandle(link.sourcePort),
          targetHandle: sanitizeHandle(link.targetPort),
          label: link.label,
          animated: false, // TODO: Make conditional/decision branches animated
          style: { strokeWidth: 2 }
        };
      });

      return { nodes, edges };
    } catch (error: any) {
      throw new TransformError(
        `Failed to convert to React Flow: ${error.message}`,
        error
      );
    }
  }

  /**
   * Convert React Flow format back to canonical LumaSkill
   *
   * @param nodes - React Flow nodes
   * @param edges - React Flow edges
   * @param metadata - Skill metadata (identifier, name, description)
   * @returns Canonical LumaSkill
   */
  static fromReactFlow(
    nodes: Node<ReactFlowNodeData>[],
    edges: Edge[],
    metadata: { identifier: string; name: string; description: string; validation?: ValidationResult }
  ): LumaSkill {
    try {
      // Transform React Flow nodes to LumaSkillNodes
      const skillNodes: LumaSkillNode[] = nodes.map((node) => ({
        id: node.id,
        type: node.data.nodeType,
        name: node.data.label,
        position: node.position,
        properties: node.data.properties
      }));

      // Transform React Flow edges to LumaSkillLinks
      const links: LumaSkillLink[] = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourcePort: edge.sourceHandle ?? undefined,
        targetPort: edge.targetHandle ?? undefined,
        label: edge.label as string | undefined
      }));

      return {
        identifier: metadata.identifier,
        name: metadata.name,
        description: metadata.description,
        nodes: skillNodes,
        links,
        validation: metadata.validation
      };
    } catch (error: any) {
      throw new TransformError(
        `Failed to convert from React Flow: ${error.message}`,
        error
      );
    }
  }

  /**
   * Apply automatic graph layout using dagre algorithm
   *
   * @param skill - Canonical LumaSkill
   * @param options - Layout options
   * @returns LumaSkill with updated node positions
   */
  static autoLayout(
    skill: LumaSkill,
    options: {
      direction?: 'TB' | 'LR' | 'BT' | 'RL'; // Top-to-Bottom, Left-to-Right, etc.
      nodeWidth?: number;
      nodeHeight?: number;
      rankSep?: number; // Separation between ranks
      nodeSep?: number; // Separation between nodes in same rank
    } = {}
  ): LumaSkill {
    try {
      const {
        direction = 'TB',
        nodeWidth = 200,
        nodeHeight = 80,
        rankSep = 100,
        nodeSep = 50
      } = options;

      // Create dagre graph
      const g = new dagre.graphlib.Graph();
      g.setGraph({
        rankdir: direction,
        ranksep: rankSep,
        nodesep: nodeSep,
        marginx: 50,
        marginy: 50
      });
      g.setDefaultEdgeLabel(() => ({}));

      // Add nodes to dagre graph
      skill.nodes.forEach((node) => {
        g.setNode(node.id, {
          width: nodeWidth,
          height: nodeHeight
        });
      });

      // Add edges to dagre graph
      skill.links.forEach((link) => {
        g.setEdge(link.source, link.target);
      });

      // Run dagre layout algorithm
      dagre.layout(g);

      // Update node positions from dagre
      const updatedNodes = skill.nodes.map((node) => {
        const dagreNode = g.node(node.id);
        return {
          ...node,
          position: {
            x: dagreNode.x - nodeWidth / 2,
            y: dagreNode.y - nodeHeight / 2
          }
        };
      });

      return {
        ...skill,
        nodes: updatedNodes
      };
    } catch (error: any) {
      console.error('Auto-layout failed, returning original skill:', error);
      // Return original skill if layout fails (graceful degradation)
      return skill;
    }
  }

  /**
   * Convert canonical LumaSkill back to OOTB format
   * (For future export functionality)
   *
   * @param skill - Canonical LumaSkill
   * @returns OOTB format skill JSON
   */
  static toOOTBFormat(skill: LumaSkill): any {
    try {
      // Convert nodes to cells
      const nodeCells = skill.nodes.map((node) => {
        const ootbType = NODE_TYPE_TO_OOTB[node.type];
        if (!ootbType) {
          throw new TransformError(`Unknown node type: ${node.type}`);
        }

        return {
          type: ootbType,
          id: node.id,
          position: node.position,
          definition: {
            name: node.name,
            ...node.properties._fullDefinition // Restore original definition
          },
          ports: this.generatePorts(node.type)
        };
      });

      // Convert links to link cells
      const linkCells = skill.links.map((link) => ({
        type: 'link',
        id: link.id,
        source: {
          id: link.source,
          port: link.sourcePort
        },
        target: {
          id: link.target,
          port: link.targetPort
        },
        labels: link.label ? [{
          attrs: {
            text: {
              text: link.label
            }
          }
        }] : undefined
      }));

      return {
        identifier: skill.identifier,
        name: skill.name,
        description: skill.description,
        designerFlowDefinition: {
          cells: [...nodeCells, ...linkCells]
        },
        validation: skill.validation
      };
    } catch (error: any) {
      throw new TransformError(
        `Failed to convert to OOTB format: ${error.message}`,
        error
      );
    }
  }

  /**
   * Generate port configuration for a node type
   * (Simplified version - expand as needed)
   */
  private static generatePorts(nodeType: NodeType): any {
    // Terminal nodes have no output port
    if (nodeType === NodeType.END || nodeType === NodeType.SWITCH_SKILL) {
      return {
        items: [
          { id: 'in1', group: 'in' }
        ]
      };
    }

    // Most nodes have both input and output
    return {
      items: [
        { id: 'in1', group: 'in' },
        { id: 'out1', group: 'out' }
      ]
    };
  }
}
